import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatNumber, formatDate, formatCurrency } from '../utils/numberFormatter';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useNavigate } from 'react-router-dom';
import '../styles/bitacora.css';
import '../styles/sales.css';
import '../styles/saleStatusBadges.css';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import ObservationSaleDetailModal from '../components/Observations/ObservationSaleDetailModal';
import SaleDetailModal from '../components/Sales/SaleDetailModal';
import { Row, Col } from 'react-bootstrap';
import { TableCard } from '../components/ui';
import {
  getApprovalStatusClass,
  getApprovalStatusLabel,
  getSettlementStatusClass,
  getSettlementStatusLabel,
} from '../utils/saleStatusAdapter';
import { getSaleLoanIdentifierDisplay } from '../utils/saleLoanIdentifier';
import {
  ApprovalStatusColumnHeader,
  SettlementStatusColumnHeader,
} from '../components/Sales/SaleStatusColumnHeaders';

const MySwal = withReactContent(Swal);

const INITIAL_OBSERVATION_FILTERS = {
  search: '',
  status: '',
  from_date: '',
  to_date: ''
};

const ObservationPage = () => {
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [saleDetailToShow, setSaleDetailToShow] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 25,
    total: 0,
    last_page: 1
  });
  const [filters, setFilters] = useState({ ...INITIAL_OBSERVATION_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...INITIAL_OBSERVATION_FILTERS });

  // Estados permitidos de observación (solo Pendiente y En Proceso)
  const observationStatuses = [
    { value: 'pending', label: 'Pendiente' },
    { value: 'in_progress', label: 'En Proceso' }
  ];

  useEffect(() => {
    fetchObservations();
  }, [appliedFilters, pagination.current_page]);

  const fetchObservations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        search: appliedFilters.search,
        status: appliedFilters.status,
        date_from: appliedFilters.from_date,
        date_to: appliedFilters.to_date,
        page: pagination.current_page,
        per_page: pagination.per_page
      });

      const response = await api.get(`/observations?${params}`);
      setSales(response.data.observations || []);
      setPagination(response.data.pagination || {
        current_page: 1,
        per_page: 25,
        total: 0,
        last_page: 1
      });
    } catch (error) {
      console.error('Error al obtener observaciones:', error);
      MySwal.fire({
        title: 'Error',
        text: 'No se pudieron cargar las observaciones',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({ ...INITIAL_OBSERVATION_FILTERS });
    setAppliedFilters({ ...INITIAL_OBSERVATION_FILTERS });
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleGoToCustomer = (dni) => {
    navigate(`/clientes/${dni}`);
  };

  const handleViewDetail = (sale) => {
    setSelectedSale(sale);
    setShowDetailModal(true);
  };

  // Componente Skeleton para la tabla
  const TableSkeleton = () => (
    <>
      {Array.from({ length: pagination.per_page }).map((_, index) => (
        <tr key={index}>
          <td><Skeleton width={40} height={30} /></td>
          <td><Skeleton width={80} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td className="text-center">
            <Skeleton width={50} height={25} />
          </td>
          <td>
            <div className="d-flex gap-2">
              <Skeleton width={30} height={30} />
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  // Componente de Paginación
  const Pagination = ({ loading }) => (
    <nav aria-label="Navegación de páginas">
      <ul className="pagination justify-content-center mb-0">
        <li className={`page-item ${loading || pagination.current_page === 1 ? 'disabled' : ''}`}>
          <button 
            className="page-link" 
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
            disabled={loading}
          >
            Anterior
          </button>
        </li>
        {Array.from({ length: pagination.last_page }, (_, i) => (
          <li key={i} className={`page-item ${pagination.current_page === i + 1 ? 'active' : ''} ${loading ? 'disabled' : ''}`}>
            <button 
              className="page-link"
              onClick={() => setPagination(prev => ({ ...prev, current_page: i + 1 }))}
              disabled={loading}
            >
              {i + 1}
            </button>
          </li>
        ))}
        <li className={`page-item ${loading || pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
          <button 
            className="page-link"
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
            disabled={loading}
          >
            Siguiente
          </button>
        </li>
      </ul>
    </nav>
  );

  return (
    <div className="page-content observations-page">
      <div className="page-title">
        <div className="row">
          <div className="col-12 col-md-6 order-md-1 order-last">
            <h3>Listado de Observaciones</h3>
            <p className="text-subtitle text-muted">
              Ventas observadas pendientes de corrección o validación.
            </p>
          </div>
        </div>
      </div>

      <section className="section">
        <TableCard
          toolbar={
            <div className="sales-filters-card">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleApplyFilters();
                }}
              >
                <Row className="g-3 align-items-end">
                  <Col xs={12} lg={4}>
                    <label htmlFor="observations-search" className="form-label sales-filters-card__label">
                      Buscar
                    </label>
                    <input
                      type="text"
                      id="observations-search"
                      className="form-control"
                      placeholder="Cliente o DNI..."
                      name="search"
                      value={filters.search}
                      onChange={handleFilterChange}
                    />
                  </Col>
                  <Col xs={12} sm={6} lg={4}>
                    <label htmlFor="observations-from-date" className="form-label sales-filters-card__label">
                      Fecha desde
                    </label>
                    <input
                      type="date"
                      id="observations-from-date"
                      className="form-control"
                      name="from_date"
                      value={filters.from_date}
                      onChange={handleFilterChange}
                    />
                  </Col>
                  <Col xs={12} sm={6} lg={4}>
                    <label htmlFor="observations-to-date" className="form-label sales-filters-card__label">
                      Fecha hasta
                    </label>
                    <input
                      type="date"
                      id="observations-to-date"
                      className="form-control"
                      name="to_date"
                      value={filters.to_date}
                      onChange={handleFilterChange}
                    />
                  </Col>
                </Row>

                <div className="sales-filters-actions sales-filters-actions--filters-only">
                  <div className="sales-filters-actions__left">
                    <button
                      type="submit"
                      className="btn btn-light-primary"
                      disabled={loading}
                    >
                      <i className="bi bi-funnel me-1" aria-hidden></i>
                      Filtrar
                    </button>
                    <button
                      type="button"
                      className="btn btn-light-secondary"
                      onClick={handleClearFilters}
                      disabled={loading}
                    >
                      <i className="bi bi-x-circle me-1" aria-hidden></i>
                      Limpiar
                    </button>
                  </div>
                </div>
              </form>
            </div>
          }
          table={
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>N° Prestamo</th>
                  <th>Cliente</th>
                  <th>Fecha</th>
                  <th>Vendedor</th>
                  <th>Monto</th>
                  <th>Cuotas</th>
                  <ApprovalStatusColumnHeader />
                  <SettlementStatusColumnHeader />
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton />
                ) : sales.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center text-muted py-5">
                      <i className="bi bi-inbox rg-empty-icon-lg"></i>
                      <p className="mt-3 mb-0">No hay observaciones para mostrar</p>
                    </td>
                  </tr>
                ) : (
                  sales.map((sale) => (
                    <tr key={sale.id}>
                      <td>{getSaleLoanIdentifierDisplay(sale)}</td>
                      <td>
                        <button
                          type="button"
                          onClick={(e) => {
                            handleGoToCustomer(sale.customer?.dni);
                          }}
                          className="rg-link-button"
                        >
                          {sale.customer?.name || 'N/A'}
                        </button>
                        <br />
                        <small className="text-muted">
                          DNI: {sale.customer?.dni || 'N/A'}
                        </small>
                      </td>
                      <td>{sale.dates?.sale || formatDate(sale.dates?.created)}</td>
                      <td>{sale.seller?.full_name || 'N/A'}</td>
                      <td>{sale.amount?.formatted || formatCurrency(sale.amount?.value || 0)}</td>
                      <td className="text-center">{sale.installments || 'N/A'}</td>
                      <td className="text-center">
                        <span className={`sale-status-badge ${getApprovalStatusClass(sale)}`}>
                          {getApprovalStatusLabel(sale) || 'N/A'}
                        </span>
                      </td>
                      <td className="text-center">
                        {getSettlementStatusLabel(sale) === '—' ? (
                          <span className="text-muted">—</span>
                        ) : (
                          <span className={`sale-status-badge ${getSettlementStatusClass(sale)}`}>
                            {getSettlementStatusLabel(sale)}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="btn-group">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            title="Ver detalle de la observación"
                            onClick={() => handleViewDetail(sale)}
                            aria-label="Ver observación"
                          >
                            <i className="bi bi-exclamation-circle me-1" aria-hidden></i>
                            Observación
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            title="Ver detalle de la venta"
                            onClick={() => setSaleDetailToShow(sale)}
                            aria-label="Ver detalle de la venta"
                          >
                            <i className="bi bi-eye me-1" aria-hidden></i>
                            Venta
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          }
          pagination={<Pagination loading={loading} />}
        />

        {/* En este flujo las notas de observación no se eliminan desde la UI (solo frontend). */}
        {showDetailModal && selectedSale && (
          <ObservationSaleDetailModal
            observationId={selectedSale.observation_id}
            saleId={selectedSale.id}
            customerId={selectedSale.customer?.uid}
            sale={selectedSale}
            onSaleStatusUpdate={(saleId, patch) => {
              setSales((prevSales) =>
                prevSales.map((item) => {
                  if (item.id !== saleId) {
                    return item;
                  }

                  return {
                    ...item,
                    ...patch,
                    observation: patch.observation
                      ? { ...item.observation, ...patch.observation }
                      : item.observation,
                  };
                })
              );
              setSelectedSale((prev) => {
                if (!prev || prev.id !== saleId) {
                  return prev;
                }

                return {
                  ...prev,
                  ...patch,
                  observation: patch.observation
                    ? { ...prev.observation, ...patch.observation }
                    : prev.observation,
                };
              });
            }}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedSale(null);
            }}
          />
        )}

        {saleDetailToShow && saleDetailToShow.customer?.id != null && (
          <SaleDetailModal
            saleId={saleDetailToShow.id}
            customerId={saleDetailToShow.customer.id}
            onClose={() => setSaleDetailToShow(null)}
          />
        )}
      </section>
    </div>
  );
};

export default ObservationPage;

